import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotify } from "ra-core";
import {
  Camera,
  Check,
  Copy,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  MapPin,
  QrCode,
  RefreshCw,
  Search,
} from "lucide-react";
import QRCode from "qrcode";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  getGuerillaCodeImages,
  listGuerillaCodes,
  type SnapAdminCode,
  type SnapCodeImage,
} from "./guerillaClient";

const STATUS_META: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    className?: string;
  }
> = {
  minted: { label: "Minted", variant: "secondary" },
  // `opened` = the capture station opened the code at the booth, NOT a guest QR
  // scan. The old "Scanned" label conflated the two; real guest scans are the
  // scan_count-derived "QR scanned" tile below.
  opened: { label: "Opened at booth", variant: "outline" },
  locked: { label: "Uploading", variant: "outline" },
  ready: { label: "Ready", variant: "default" },
  claimed: {
    label: "Generating",
    variant: "default",
    className: "bg-emerald-600 hover:bg-emerald-600",
  },
  delivered: {
    label: "Delivered",
    variant: "default",
    className: "bg-emerald-700 hover:bg-emerald-700",
  },
  expired: { label: "Expired", variant: "destructive" },
  void: { label: "Void", variant: "destructive" },
};

// Field-friendly funnel buckets — staff think in "did the sticker work?" terms,
// not raw lifecycle states.
const BOUND = new Set(["ready", "claimed", "delivered"]); // a photo is attached
const STARTED = new Set(["claimed", "delivered"]); // guest scanned + sent the code

const STATUS_FILTERS: {
  key: string;
  label: string;
  match: (code: SnapAdminCode) => boolean;
}[] = [
  { key: "all", label: "All", match: () => true },
  {
    key: "unused",
    label: "Unused",
    match: (c) => ["minted", "opened", "locked"].includes(c.status),
  },
  { key: "ready", label: "Awaiting scan", match: (c) => c.status === "ready" },
  { key: "active", label: "In progress", match: (c) => c.status === "claimed" },
  {
    key: "delivered",
    label: "Delivered",
    match: (c) => c.status === "delivered",
  },
  {
    key: "problem",
    label: "Problem",
    match: (c) => ["expired", "void"].includes(c.status),
  },
];

function formatDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? {
    label: status,
    variant: "outline" as const,
  };
  return (
    <Badge variant={meta.variant} className={meta.className}>
      {meta.label}
    </Badge>
  );
}

async function downloadQr(code: SnapAdminCode) {
  // The campaign's QR points straight at WhatsApp with the SNP code prefilled
  // (the portal is optional), so re-printing from the field uses wa_link.
  const dataUrl = await QRCode.toDataURL(code.wa_link, {
    width: 1024,
    margin: 2,
    errorCorrectionLevel: "M",
  });
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = `${code.code}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-card p-3 text-center">
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

type RowActions = {
  onCopy: (code: SnapAdminCode) => void;
  onQr: (code: SnapAdminCode) => void;
  /**
   * Fetch the short-lived SIGNED photo URLs for a code (lazy, on viewer-open).
   * Optional so the pure view stays testable without a network stub; when absent
   * the media count renders as plain text instead of a click-to-view badge.
   */
  onViewImages?: (code: SnapAdminCode) => Promise<SnapCodeImage[]>;
};

/**
 * Pure presentation of the campaign funnel: stat tiles, search/booth/status
 * filters, and the responsive code list. Data + side effects (fetch, clipboard,
 * notifications) live in <GuerillaPage>, which keeps this testable with plain
 * props (mirrors EchoAtelier).
 */
export function GuerillaCodesView({
  codes,
  loading,
  error,
  onCopy,
  onQr,
  onViewImages,
}: {
  codes: SnapAdminCode[];
  loading: boolean;
  error: string | null;
} & RowActions) {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [booth, setBooth] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dev codes are reusable internal tools — keep them out of the field funnel.
  const guestCodes = useMemo(() => codes.filter((c) => !c.dev), [codes]);

  const booths = useMemo(() => {
    const set = new Set<string>();
    for (const code of guestCodes) {
      if (code.booth) set.add(code.booth);
    }
    return Array.from(set).sort();
  }, [guestCodes]);

  const summary = useMemo(() => {
    const scoped =
      booth === "all"
        ? guestCodes
        : guestCodes.filter((c) => c.booth === booth);
    // A guest QR scan is counted as distinct (scan_count >= 1), so a guest
    // re-scanning their own sticker is still one person at the stage.
    const scanned = scoped.filter((c) => (c.scan_count ?? 0) >= 1);
    return {
      total: scoped.length,
      bound: scoped.filter((c) => BOUND.has(c.status)).length,
      started: scoped.filter((c) => STARTED.has(c.status)).length,
      delivered: scoped.filter((c) => c.status === "delivered").length,
      scanned: scanned.length,
      // Scanned the QR but never sent the WhatsApp message (never claimed).
      scannedNoWhatsApp: scanned.filter(
        (c) => !["claimed", "delivered"].includes(c.status),
      ).length,
    };
  }, [guestCodes, booth]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const statusMatch =
      STATUS_FILTERS.find((f) => f.key === statusFilter) ?? STATUS_FILTERS[0];
    return guestCodes.filter((code) => {
      if (booth !== "all" && code.booth !== booth) return false;
      if (!statusMatch.match(code)) return false;
      if (needle) {
        const haystack = `${code.code} ${code.wa_id ?? ""} ${
          code.booth ?? ""
        }`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [guestCodes, search, booth, statusFilter]);

  return (
    <CardContent className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Codes" value={summary.total} />
        <StatTile label="QR scanned" value={summary.scanned} />
        <StatTile
          label="Scanned, no WhatsApp"
          value={summary.scannedNoWhatsApp}
        />
        <StatTile label="Photo bound" value={summary.bound} />
        <StatTile label="Guest started" value={summary.started} />
        <StatTile label="Video sent" value={summary.delivered} />
      </div>

      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code, phone, or booth…"
              className="pl-8"
              inputMode="search"
            />
          </div>
          <Select value={booth} onValueChange={setBooth}>
            <SelectTrigger className="sm:w-56">
              <SelectValue placeholder="All booths" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All booths</SelectItem>
              {booths.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={statusFilter === f.key ? "default" : "outline"}
              className="shrink-0"
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load Snap codes</AlertTitle>
          <AlertDescription>
            {error}. The bot must be deployed with SNAP_ENABLED and the CRM
            needs SNAP_BOT_BASE_URL + SNAP_PORTAL_API_KEY on its Vercel project
            (see src/server/snapProxy.ts).
          </AlertDescription>
        </Alert>
      ) : isMobile ? (
        <MobileList
          codes={filtered}
          loading={loading}
          onCopy={onCopy}
          onQr={onQr}
          onViewImages={onViewImages}
        />
      ) : (
        <DesktopTable
          codes={filtered}
          loading={loading}
          onCopy={onCopy}
          onQr={onQr}
          onViewImages={onViewImages}
        />
      )}
    </CardContent>
  );
}

export function GuerillaPage() {
  const notify = useNotify();
  const isMobile = useIsMobile();
  const [codes, setCodes] = useState<SnapAdminCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCodes(await listGuerillaCodes());
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCopy = useCallback(
    async (code: SnapAdminCode) => {
      try {
        await navigator.clipboard.writeText(code.wa_link);
        notify(`${code.code} WhatsApp link copied`, { type: "info" });
      } catch {
        notify("Could not copy the link", { type: "error" });
      }
    },
    [notify],
  );

  const handleQr = useCallback(
    async (code: SnapAdminCode) => {
      try {
        await downloadQr(code);
      } catch {
        notify("QR generation failed", { type: "error" });
      }
    },
    [notify],
  );

  // Sign on demand: the viewer asks for fresh presigned URLs only when opened.
  const handleViewImages = useCallback(
    (code: SnapAdminCode) => getGuerillaCodeImages(code.token),
    [],
  );

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <Camera className="size-5" />
              Guerilla — Snap campaign
            </CardTitle>
            <CardDescription>
              Live status of every Instax QR code: scanned → photo bound → guest
              started → video delivered. Use it at the booth to debug a guest's
              code.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size={isMobile ? "icon" : "default"}
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw className={loading ? "animate-spin" : ""} />
            {!isMobile && "Refresh"}
          </Button>
        </CardHeader>
        <GuerillaCodesView
          codes={codes}
          loading={loading}
          error={error}
          onCopy={handleCopy}
          onQr={handleQr}
          onViewImages={handleViewImages}
        />
      </Card>
    </div>
  );
}

function ActionButtons({
  code,
  onCopy,
  onQr,
}: { code: SnapAdminCode } & RowActions) {
  return (
    <div className="flex gap-1">
      <Button
        variant="ghost"
        size="icon"
        title="Copy WhatsApp link"
        onClick={() => onCopy(code)}
      >
        <Copy />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        title="Download QR code"
        onClick={() => onQr(code)}
      >
        <QrCode />
      </Button>
      <Button variant="ghost" size="icon" title="Open in WhatsApp" asChild>
        {/* wa_direct = raw wa.me, so an admin click never counts as a guest scan. */}
        <a
          href={code.wa_direct ?? code.wa_link}
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLink />
        </a>
      </Button>
    </div>
  );
}

/**
 * The "Media" affordance: a camera badge showing how many photos are bound, and
 * — when there's at least one and a fetcher is wired — a click-to-view dialog of
 * the photo(s). The images live in the bot's private quarantine bucket; the
 * dialog lazily requests short-lived SIGNED URLs (never public) and drops them on
 * close so a re-open always re-signs (they expire within the hour).
 */
function CodeMediaCell({
  code,
  onViewImages,
  variant = "table",
}: {
  code: SnapAdminCode;
  onViewImages?: (code: SnapAdminCode) => Promise<SnapCodeImage[]>;
  variant?: "table" | "list";
}) {
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState<SnapCodeImage[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasImage = code.media_count > 0;

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) {
        // Drop the signed URLs on close; a re-open re-signs fresh ones.
        setImages(null);
        setError(null);
        return;
      }
      if (!onViewImages) return;
      setLoading(true);
      setError(null);
      onViewImages(code)
        .then(setImages)
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : String(cause)),
        )
        .finally(() => setLoading(false));
    },
    [code, onViewImages],
  );

  // No photo bound, or no fetcher wired (isolated tests): static display.
  if (!hasImage || !onViewImages) {
    if (variant === "list") {
      return <span>📷 {code.media_count}</span>;
    }
    return hasImage ? (
      <span className="tabular-nums">{code.media_count}</span>
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  }

  const label = `View photo for ${code.code}`;
  const trigger =
    variant === "list" ? (
      <button
        type="button"
        aria-label={label}
        className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
      >
        <ImageIcon className="size-3" />
        {code.media_count} photo{code.media_count === 1 ? "" : "s"}
      </button>
    ) : (
      <Button
        variant="outline"
        size="sm"
        aria-label={label}
        title={label}
        className="h-7 gap-1 px-2"
      >
        <ImageIcon className="size-3.5" />
        {code.media_count}
      </Button>
    );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-mono">{code.code}</DialogTitle>
          <DialogDescription>
            Private photo-preview links refresh whenever you reopen this viewer.
            Printed QR codes do not expire.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading photo…
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertTitle>Could not load the photo</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : images && images.length > 0 ? (
          <div className="space-y-3">
            {images.map((img, i) => (
              <a
                key={`${code.token}-${i}`}
                href={img.url}
                target="_blank"
                rel="noreferrer"
                className="block"
              >
                <img
                  src={img.url}
                  alt={`${code.code} photo ${i + 1}`}
                  className="max-h-[70vh] w-full rounded-md border object-contain"
                />
              </a>
            ))}
          </div>
        ) : (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No photo is bound to this code yet.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ loading }: { loading: boolean }) {
  return (
    <div className="py-10 text-center text-sm text-muted-foreground">
      {loading ? "Loading…" : "No codes match these filters."}
    </div>
  );
}

function MobileList({
  codes,
  loading,
  onCopy,
  onQr,
  onViewImages,
}: { codes: SnapAdminCode[]; loading: boolean } & RowActions) {
  if (codes.length === 0) return <EmptyState loading={loading} />;
  return (
    <div className="space-y-2">
      {codes.map((code) => (
        <div key={code.token} className="rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-sm font-medium">{code.code}</span>
            <StatusBadge status={code.status} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {code.booth ? (
              <span className="flex items-center gap-1">
                <MapPin className="size-3" />
                {code.booth}
              </span>
            ) : null}
            <CodeMediaCell
              code={code}
              onViewImages={onViewImages}
              variant="list"
            />
            {code.has_consent ? (
              <span className="flex items-center gap-1 text-emerald-600">
                <Check className="size-3" />
                consent
              </span>
            ) : null}
          </div>
          {code.wa_id ? (
            <div className="mt-1 font-mono text-xs">
              {code.wa_id}
              <span className="ml-2 text-muted-foreground">
                {formatDate(code.claimed_at)}
              </span>
            </div>
          ) : null}
          <div className="mt-1 text-[0.7rem] text-muted-foreground">
            created {formatDate(code.created_at)}
            {code.status === "delivered"
              ? ` · delivered ${formatDate(code.delivered_at)}`
              : ""}
          </div>
          <div className="mt-2 flex justify-end">
            <ActionButtons code={code} onCopy={onCopy} onQr={onQr} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DesktopTable({
  codes,
  loading,
  onCopy,
  onQr,
  onViewImages,
}: { codes: SnapAdminCode[]; loading: boolean } & RowActions) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Code</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Booth</TableHead>
          <TableHead>Guest</TableHead>
          <TableHead className="text-right">Media</TableHead>
          <TableHead className="text-center">Consent</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {codes.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8}>
              <EmptyState loading={loading} />
            </TableCell>
          </TableRow>
        ) : (
          codes.map((code) => (
            <TableRow key={code.token}>
              <TableCell className="font-mono font-medium">
                {code.code}
              </TableCell>
              <TableCell>
                <StatusBadge status={code.status} />
              </TableCell>
              <TableCell>{code.booth || "—"}</TableCell>
              <TableCell>
                {code.wa_id ? (
                  <>
                    <div className="font-mono text-sm">{code.wa_id}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(code.claimed_at)}
                    </div>
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end">
                  <CodeMediaCell code={code} onViewImages={onViewImages} />
                </div>
              </TableCell>
              <TableCell className="text-center">
                {code.has_consent ? (
                  <Check className="mx-auto size-4 text-emerald-600" />
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">
                {formatDate(code.created_at)}
              </TableCell>
              <TableCell>
                <div className="flex justify-end">
                  <ActionButtons code={code} onCopy={onCopy} onQr={onQr} />
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

export default GuerillaPage;
